const PERSONAL_CF = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
const COMPANY_CF = /^\d{11}$/;
const FOREIGN_TAX_ID = /^[A-Z0-9][A-Z0-9 .\-\/]{2,28}[A-Z0-9]$/i;
const EMAIL = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;

const CF_ODD: { [char: string]: number } = {
    '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
    'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
    'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
    'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
};

const CF_EVEN: { [char: string]: number } = {
    '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
    'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
    'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
    'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
};

export function normalizeFiscalCode(value: string): string {
    return (value || '').replace(/\s+/g, '').toUpperCase();
}

export function isItalianFiscalCode(value: string): boolean {
    const cf = normalizeFiscalCode(value);
    if (COMPANY_CF.test(cf)) {
        return isValidPartitaIva(cf);
    }
    if (PERSONAL_CF.test(cf)) {
        return isValidPersonalCf(cf);
    }
    return false;
}

export function fiscalCodeError(value: string, foreignSupplier: boolean): string | null {
    const raw = (value || '').trim();
    if (!raw) {
        return 'Inserisci il codice fiscale.';
    }
    if (foreignSupplier) {
        return FOREIGN_TAX_ID.test(raw)
            ? null
            : 'Per un fornitore estero inserisci un identificativo di 4-30 caratteri (lettere, numeri, spazi, . - /).';
    }
    return isItalianFiscalCode(raw)
        ? null
        : 'Il codice fiscale non è nel formato italiano: 11 cifre per una società, oppure 16 caratteri per una persona fisica. Se il fornitore è estero, seleziona "Fornitore estero".';
}

export function splitEmails(value: string): string[] {
    return (value || '')
        .split(';')
        .map(part => part.trim())
        .filter(part => part.length > 0);
}

export function joinEmails(emails: string[]): string {
    return (emails || [])
        .map(part => (part || '').trim())
        .filter(part => part.length > 0)
        .join(';');
}

export function singleEmailError(value: string): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return 'Inserisci un indirizzo email.';
    }
    if (!EMAIL.test(trimmed)) {
        return 'Indirizzo email non valido.';
    }
    return null;
}

export function emailListError(emails: string[]): string | null {
    const list = (emails || []).map(part => (part || '').trim());
    const nonEmpty = list.filter(part => part.length > 0);
    if (nonEmpty.length === 0) {
        return 'Inserisci almeno un indirizzo email.';
    }
    if (list.some(part => part.length === 0)) {
        return 'Compila tutti i campi email aggiunti oppure rimuovi quelli vuoti.';
    }
    if (nonEmpty.some(part => !EMAIL.test(part))) {
        return 'Uno o più indirizzi email non sono validi.';
    }
    return null;
}

export function emailError(value: string): string | null {
    const parts = splitEmails(value);
    if (parts.length === 0) {
        return 'Inserisci almeno un indirizzo email.';
    }
    if (parts.some(part => !EMAIL.test(part))) {
        return 'Uno o più indirizzi non sono email valide.';
    }
    return null;
}

export function normalizeEmails(value: string): string {
    return splitEmails(value).join(';');
}

function isValidPartitaIva(value: string): boolean {
    let sum = 0;
    for (let i = 0; i < 10; i++) {
        let digit = parseInt(value[i], 10);
        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
    }
    return ((10 - (sum % 10)) % 10) === parseInt(value[10], 10);
}

function isValidPersonalCf(value: string): boolean {
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        sum += (i % 2 === 0) ? CF_ODD[value[i]] : CF_EVEN[value[i]];
    }
    return value[15] === String.fromCharCode(65 + (sum % 26));
}
